import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNotification } from '../contexts/NotificationContext';
import { apiClient } from '../utils/apiClient';
import ProfileSelection from '../components/ProfileSelection';
import Workspace from '../components/Workspace';
import './UserProfile.css';

const UserProfile = () => {
  const { logout } = useAuth();
  const { isDarkMode } = useTheme();
  const { showNotification } = useNotification();
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [deletePassword, setDeletePassword] = useState('');
  
  // Access token state
  const [accessTokens, setAccessTokens] = useState([]);
  const [showTokenForm, setShowTokenForm] = useState(false);
  const [newTokenData, setNewTokenData] = useState({
    name: '',
    expiresInDays: ''
  });
  const [createdToken, setCreatedToken] = useState(null);
  const [showCreatedToken, setShowCreatedToken] = useState(false);
  
  // Connect to AI state
  const [proxyJsonConfig, setProxyJsonConfig] = useState('');
  const [profileTools, setProfileTools] = useState(null);
  const [loadingTools, setLoadingTools] = useState(false);
  const [expandedResources, setExpandedResources] = useState(new Set());
  const activeProfileIdRef = useRef(null);

  // Generate JSON configuration for MCP Proxy
  const generateProxyJsonConfig = useCallback(() => {
    const config = {
      mcpServers: {
        "AI-MCP-Bio": {
          url: "https://mcp.ai-mcp-bio.com/mcp",
          headers: {
            "Authorization": "Bearer YOUR_ACCESS_TOKEN_HERE"
          }
        }
      }
    };

    setProxyJsonConfig(JSON.stringify(config, null, 2));
  }, []);

  useEffect(() => {
    loadProfileData();
    loadAccessTokens();
  }, []);

  // Generate proxy config on mount
  useEffect(() => {
    generateProxyJsonConfig();
  }, [generateProxyJsonConfig]);

  // Load tools for active profile
  const loadProfileTools = useCallback(async (forceReload = false) => {
    const activeProfile = profiles?.find(profile => profile.isActive);
    
    // If no active profile, only clear tools if we previously had a profile
    if (!activeProfile) {
      if (activeProfileIdRef.current !== null) {
        setProfileTools(null);
        activeProfileIdRef.current = null;
      }
      return;
    }

    // Only reload if the active profile ID has changed or if forced
    if (!forceReload && activeProfileIdRef.current === activeProfile.id) {
      return; // Same profile, don't reload
    }

    // Update the ref before making the API call
    activeProfileIdRef.current = activeProfile.id;

    try {
      setLoadingTools(true);
      const response = await apiClient.get('/profiles/tools');
      console.log('Profile tools response:', response.data);
      setProfileTools(response.data.tools);
    } catch (error) {
      console.error('Error loading profile tools:', error);
      // On error, only clear if this was a new profile load
      // Otherwise keep the previous tools
      if (forceReload || activeProfileIdRef.current === activeProfile.id) {
        // Only set to null if we don't have tools yet
        setProfileTools(prev => prev || null);
      }
    } finally {
      setLoadingTools(false);
    }
  }, [profiles]);

  // Load tools when active profile changes
  useEffect(() => {
    const activeProfile = profiles?.find(profile => profile.isActive);
    const currentActiveId = activeProfile?.id || null;
    
    // Only reload if the active profile ID actually changed
    if (activeProfileIdRef.current !== currentActiveId) {
      loadProfileTools(true);
    }
  }, [profiles, loadProfileTools]);

  // Toggle resource expansion
  const toggleResource = (resourceId) => {
    setExpandedResources(prev => {
      const newSet = new Set(prev);
      if (newSet.has(resourceId)) {
        newSet.delete(resourceId);
      } else {
        newSet.add(resourceId);
      }
      return newSet;
    });
  };

  // Copy text to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log('Copied to clipboard');
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // Get active profile info for display
  const getActiveProfileInfo = () => {
    if (!profiles || profiles.length === 0) return null;
    const activeProfile = profiles.find(profile => profile.isActive);
    return activeProfile;
  };

  const activeProfile = getActiveProfileInfo();

  const loadProfileData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/profiles');
      setProfileData(response.data.user);
      setProfiles(response.data.profiles);
      
      // Select default profile if available
      const defaultProfile = response.data.profiles.find(p => p.isDefault);
      if (defaultProfile) {
        setSelectedProfile(defaultProfile);
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAccessTokens = async () => {
    try {
      const response = await apiClient.get('/profiles/access-tokens');
      setAccessTokens(response.data.tokens);
    } catch (error) {
      console.error('Error loading access tokens:', error);
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    if (!newProfileName.trim()) return;

    try {
      setSaving(true);
      const response = await apiClient.post('/profiles', {
        name: newProfileName.trim()
      });
      
      setProfiles(prev => [...prev, response.data.profile]);
      setNewProfileName('');
    } catch (error) {
      console.error('Error creating profile:', error);
      alert(error.response?.data?.error || 'Failed to create profile');
    } finally {
      setSaving(false);
    }
  };

  const handleEditProfile = (profile) => {
    setSelectedProfile(profile);
    setIsEditing(true);
  };

  const handleDeleteProfile = async (profile) => {
    if (!window.confirm(`Are you sure you want to delete "${profile.name}"?`)) {
      return;
    }

    try {
      await apiClient.delete(`/profiles/${profile.id}`);
      setProfiles(prev => prev.filter(p => p.id !== profile.id));
      
      if (selectedProfile?.id === profile.id) {
        const remainingProfiles = profiles.filter(p => p.id !== profile.id);
        setSelectedProfile(remainingProfiles[0] || null);
      }
      
      // Reload profile data to get updated active status
      loadProfileData();
    } catch (error) {
      console.error('Error deleting profile:', error);
      alert(error.response?.data?.error || 'Failed to delete profile');
    }
  };

  const handleSetActiveProfile = async (profile) => {
    try {
      await apiClient.post(`/profiles/${profile.id}/set-active`);
      
      // Reload profile data to get updated active status
      loadProfileData();
      
      showNotification(`${profile.name} is now the active profile`, 'success', 3000);
    } catch (error) {
      console.error('Error setting active profile:', error);
      showNotification(error.response?.data?.error || 'Failed to set active profile', 'error', 4000);
    }
  };

  const handleProfileUpdate = () => {
    loadProfileData();
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      alert('New password must be at least 6 characters long');
      return;
    }

    try {
      setSaving(true);
      await apiClient.post('/profiles/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      alert('Password changed successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordForm(false);
    } catch (error) {
      console.error('Error changing password:', error);
      alert(error.response?.data?.error || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      await apiClient.post('/profiles/delete-account', {
        password: deletePassword
      });
      
      alert('Account deleted successfully');
      logout();
    } catch (error) {
      console.error('Error deleting account:', error);
      alert(error.response?.data?.error || 'Failed to delete account');
    } finally {
      setSaving(false);
    }
  };

  // Access token management functions
  const handleCreateToken = async (e) => {
    e.preventDefault();
    
    if (!newTokenData.name.trim()) {
      alert('Token name is required');
      return;
    }

    try {
      setSaving(true);
      const response = await apiClient.post('/profiles/access-tokens', {
        name: newTokenData.name.trim(),
        expiresInDays: newTokenData.expiresInDays ? parseInt(newTokenData.expiresInDays) : null
      });
      
      setCreatedToken(response.data.token);
      setShowCreatedToken(true);
      setNewTokenData({ name: '', expiresInDays: '' });
      setShowTokenForm(false);
      loadAccessTokens();
    } catch (error) {
      console.error('Error creating access token:', error);
      alert(error.response?.data?.error || 'Failed to create access token');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeToken = async (tokenId) => {
    if (!window.confirm('Are you sure you want to revoke this access token?')) {
      return;
    }

    try {
      await apiClient.put(`/profiles/access-tokens/${tokenId}/revoke`);
      loadAccessTokens();
      alert('Access token revoked successfully');
    } catch (error) {
      console.error('Error revoking access token:', error);
      alert(error.response?.data?.error || 'Failed to revoke access token');
    }
  };

  const handleDeleteToken = async (tokenId) => {
    if (!window.confirm('Are you sure you want to delete this access token? This action cannot be undone.')) {
      return;
    }

    try {
      await apiClient.delete(`/profiles/access-tokens/${tokenId}`);
      loadAccessTokens();
      alert('Access token deleted successfully');
    } catch (error) {
      console.error('Error deleting access token:', error);
      alert(error.response?.data?.error || 'Failed to delete access token');
    }
  };

  const copyTokenToClipboard = async (token) => {
    try {
      await navigator.clipboard.writeText(token);
      alert('Token copied to clipboard');
    } catch (err) {
      console.error('Failed to copy token: ', err);
      alert('Failed to copy token to clipboard');
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'account':
        return (
          <div className="tab-content">
            {/* User Information Card */}
            <div className="card" style={{ marginBottom: '2rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>Account Information</h3>
              <div className="user-info-grid">
                <div className="user-info-item">
                  <strong>Username:</strong> {profileData?.username}
                </div>
                <div className="user-info-item">
                  <strong>Email:</strong> {profileData?.email}
                </div>
                <div className="user-info-item">
                  <strong>Role:</strong> {profileData?.role}
                </div>
              </div>
            </div>

            {/* Account Management */}
            <div className="profile-management">
              <div className="profile-management-header">
                <h2>Account Management</h2>
                <div className="profile-actions">
                  <button 
                    className="btn btn-primary"
                    onClick={() => setShowPasswordForm(!showPasswordForm)}
                  >
                    Change Password
                  </button>
                  <button 
                    className="btn btn-danger"
                    onClick={() => setShowDeleteAccount(!showDeleteAccount)}
                  >
                    Delete Account
                  </button>
                </div>
              </div>

              {/* Password Change Form */}
              {showPasswordForm && (
                <div className="password-form">
                  <h3>Change Password</h3>
                  <form onSubmit={handleChangePassword}>
                    <div className="form-group">
                      <label>Current Password</label>
                      <input
                        type="password"
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>New Password</label>
                      <input
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                        required
                        minLength={6}
                      />
                    </div>
                    <div className="form-group">
                      <label>Confirm New Password</label>
                      <input
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        required
                        minLength={6}
                      />
                    </div>
                    <div className="form-actions">
                      <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={() => setShowPasswordForm(false)}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="btn btn-primary"
                        disabled={saving}
                      >
                        {saving ? 'Changing...' : 'Change Password'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Delete Account Form */}
              {showDeleteAccount && (
                <div className="delete-account-form">
                  <h3>Delete Account</h3>
                  <p className="warning-text">
                    <strong>Warning:</strong> This action cannot be undone. All your data, including profiles, will be permanently deleted.
                  </p>
                  <form onSubmit={handleDeleteAccount}>
                    <div className="form-group">
                      <label>Enter your password to confirm deletion</label>
                      <input
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        required
                        placeholder="Enter your password"
                      />
                    </div>
                    <div className="form-actions">
                      <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={() => setShowDeleteAccount(false)}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="btn btn-danger"
                        disabled={saving}
                      >
                        {saving ? 'Deleting...' : 'Delete Account'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Access Token Management */}
              <div className="access-tokens-section">
                <div className="access-tokens-header">
                  <h2>Access Tokens</h2>
                  <div className="access-tokens-actions">
                    <button 
                      className="btn btn-primary"
                      onClick={() => setShowTokenForm(!showTokenForm)}
                    >
                      Create Token
                    </button>
                  </div>
                </div>

                {/* Create Token Form */}
                {showTokenForm && (
                  <div className="create-token-form">
                    <h3>Create New Access Token</h3>
                    <form onSubmit={handleCreateToken}>
                      <div className="form-group">
                        <label>Token Name</label>
                        <input
                          type="text"
                          value={newTokenData.name}
                          onChange={(e) => setNewTokenData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g., My API Client"
                          required
                          maxLength={50}
                        />
                      </div>
                      <div className="form-group">
                        <label>Expires In (Days) - Optional</label>
                        <input
                          type="number"
                          value={newTokenData.expiresInDays}
                          onChange={(e) => setNewTokenData(prev => ({ ...prev, expiresInDays: e.target.value }))}
                          placeholder="Leave empty for no expiration"
                          min="1"
                          max="365"
                        />
                        <small>Leave empty for no expiration</small>
                      </div>
                      <div className="form-actions">
                        <button 
                          type="button" 
                          className="btn btn-secondary"
                          onClick={() => setShowTokenForm(false)}
                          disabled={saving}
                        >
                          Cancel
                        </button>
                        <button 
                          type="submit" 
                          className="btn btn-primary"
                          disabled={saving}
                        >
                          {saving ? 'Creating...' : 'Create Token'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Created Token Display */}
                {showCreatedToken && createdToken && (
                  <div className="created-token-display">
                    <h3>Token Created Successfully!</h3>
                    <div className="token-warning">
                      <strong>⚠️ Important:</strong> This is the only time you'll see the full token. Copy it now and store it securely!
                    </div>
                    <div className="token-display">
                      <div className="token-value">
                        <code>{createdToken.token}</code>
                        <button 
                          className="btn btn-small btn-primary"
                          onClick={() => copyTokenToClipboard(createdToken.token)}
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                    <div className="token-info">
                      <p><strong>Name:</strong> {createdToken.name}</p>
                      {createdToken.expiresAt && (
                        <p><strong>Expires:</strong> {new Date(createdToken.expiresAt).toLocaleString()}</p>
                      )}
                      <p><strong>Created:</strong> {new Date(createdToken.createdAt).toLocaleString()}</p>
                    </div>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => setShowCreatedToken(false)}
                    >
                      Close
                    </button>
                  </div>
                )}

                {/* Access Tokens List */}
                <div className="tokens-list">
                  {accessTokens.length === 0 ? (
                    <div className="no-tokens">
                      <p>No access tokens created yet.</p>
                      <p>Create your first token to use the API programmatically.</p>
                    </div>
                  ) : (
                    <div className="tokens-grid">
                      {accessTokens.map(token => (
                        <div key={token.id} className={`token-card ${!token.isActive ? 'revoked' : ''}`}>
                          <div className="token-card-header">
                            <h4>{token.name}</h4>
                            <div className="token-status">
                              {token.isActive ? (
                                <span className="status-active">Active</span>
                              ) : (
                                <span className="status-revoked">Revoked</span>
                              )}
                            </div>
                          </div>
                          <div className="token-card-body">
                            <div className="token-preview">
                              <code>{token.token}</code>
                            </div>
                            <div className="token-details">
                              <p><strong>Created:</strong> {new Date(token.createdAt).toLocaleDateString()}</p>
                              {token.lastUsedAt && (
                                <p><strong>Last Used:</strong> {new Date(token.lastUsedAt).toLocaleDateString()}</p>
                              )}
                              {token.expiresAt && (
                                <p><strong>Expires:</strong> {new Date(token.expiresAt).toLocaleDateString()}</p>
                              )}
                            </div>
                            <div className="token-actions">
                              {token.isActive ? (
                                <button 
                                  className="btn btn-warning btn-small"
                                  onClick={() => handleRevokeToken(token.id)}
                                >
                                  Revoke
                                </button>
                              ) : (
                                <button 
                                  className="btn btn-danger btn-small"
                                  onClick={() => handleDeleteToken(token.id)}
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Usage Instructions */}
                <div className="token-usage-info">
                  <h4>How to Use Access Tokens</h4>
                  <p>Access tokens allow you to authenticate with the API programmatically. Include the token in the Authorization header:</p>
                  <div className="code-example">
                    <code>Authorization: Bearer YOUR_TOKEN_HERE</code>
                  </div>
                  <p>Use these tokens to access your profiles, MCP servers, and workflows through the API.</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'profile':
        return (
          <div className="tab-content">
            {/* Profiles List */}
            <div className="profiles-section">
              <div className="profiles-header">
                <h2>Your Profiles ({profiles.length}/5)</h2>
                {profiles.length < 5 && (
                  <form onSubmit={handleCreateProfile} className="create-profile-form">
                    <input
                      type="text"
                      value={newProfileName}
                      onChange={(e) => setNewProfileName(e.target.value)}
                      placeholder="New profile name"
                      required
                      maxLength={50}
                    />
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={saving || !newProfileName.trim()}
                    >
                      {saving ? 'Creating...' : 'Create Profile'}
                    </button>
                  </form>
                )}
              </div>

              <div className="profiles-list">
                {profiles.map(profile => (
                  <div 
                    key={profile.id} 
                    className={`profile-card ${selectedProfile?.id === profile.id ? 'selected' : ''}`}
                    onClick={() => !isEditing && setSelectedProfile(profile)}
                  >
                    <div className="profile-card-header">
                      <h3>
                        {profile.name}
                      </h3>
                      <div className="profile-badges">
                        {profile.isDefault && <span className="default-badge">Default</span>}
                        {profile.isActive && <span className="active-badge">Active</span>}
                      </div>
                    </div>
                    <div className="profile-card-body">
                      <div className="profile-card-content">
                        <p><strong>MCP Servers:</strong> {profile.mcpServers?.length || 0}</p>
                        <p><strong>Workflows:</strong> {profile.workflows?.length || 0}</p>
                        <p><strong>Created:</strong> {new Date(profile.createdAt).toLocaleDateString()}</p>
                      </div>
                      <div className="profile-card-actions">
                          <button 
                            className="btn btn-success"
                            disabled={profile.isActive}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!profile.isActive) {
                                handleSetActiveProfile(profile);
                              }
                            }}
                          >
                            {profile.isActive ? 'Active' : 'Set Active'}
                          </button>
                          {!profile.isDefault && (
                            <button 
                              className="btn btn-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteProfile(profile);
                              }}
                            >
                              Delete
                            </button>
                          )}
                          <button 
                            className="btn btn-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditProfile(profile);
                            }}
                          >
                            {isEditing && selectedProfile?.id === profile.id ? 'Editing...' : 'Edit'}
                          </button>
                        </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Profile Selection */}
            {selectedProfile && (
              <div className="profile-selection-section">
                <ProfileSelection
                  profile={selectedProfile}
                  onUpdate={handleProfileUpdate}
                  isEditing={isEditing}
                  onCancel={handleCancelEdit}
                />
              </div>
            )}
          </div>
        );

      case 'connect-ai':
        return (
          <div className="tab-content">
            <div className="connect-ai-container">
              {/* Header Section */}
              <div className="connect-ai-header">
                <h2>Connect to Cursor</h2>
                <p className="connect-ai-description">
                  Use bioinformatics tools directly from Cursor IDE with AI-MCP-Bio. Enable your AI assistant to perform 
                  real-world bioinformatics tasks through a simple, secure connection without leaving your coding environment.
                </p>
              </div>

              {/* Configuration Section */}
              <div className="config-section">
                <h3>Configuring AI-MCP-Bio in Cursor</h3>
                <p className="config-intro">
                  First try clicking the 'Add to Cursor' button below to automatically configure it! If that doesn't work 
                  or you need to modify the installation, follow the directions below.
                </p>

                {/* Automatic Configuration Button */}
                <div className="auto-config-section">
                  <button className="add-to-cursor-btn">
                    <span className="cursor-icon">⌘</span>
                    Add to Cursor
                  </button>
                </div>

                {/* Manual Configuration Steps */}
                <div className="manual-config">
                  <h4>Manual Configuration</h4>
                  <ol className="config-steps">
                    <li>Open Cursor settings (<kbd>⇧⌘+J</kbd>)</li>
                    <li>Navigate to the "MCP Tools" tab and click "New MCP Server"</li>
                    <li>Copy/paste the following JSON configuration from below, then hit <kbd>CMD+S</kbd> or <kbd>CTRL+S</kbd> to save</li>
                  </ol>
                </div>

                {/* Usage Note */}
                <div className="usage-note">
                  <p><strong>To use AI-MCP-Bio within Cursor:</strong> Set the chat to Agent mode</p>
                </div>

                {/* Security Warning */}
                <div className="security-warning">
                  <div className="warning-icon">⚠️</div>
                  <div className="warning-content">
                    <strong>Caution:</strong> Treat your MCP server URL like a password! It can be used to run tools 
                    attached to this server and access your bioinformatics data.
                  </div>
                </div>

                {/* JSON Configuration AI-MCP-Bio Proxy */}
                <div className="json-config-section" style={{ marginTop: '3rem' }}>
                  <h3>JSON Configuration AI-MCP-Bio Proxy</h3>
                  <p className="config-intro">
                    Use this simplified configuration to connect to AI-MCP-Bio through the proxy server. 
                    This single configuration provides access to all MCP servers and workflows in your active profile.
                  </p>
                  
                  <div className="proxy-instructions" style={{ 
                    marginBottom: '1.5rem', 
                    padding: '1rem', 
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
                    borderRadius: '8px',
                    border: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`
                  }}>
                    <h4 style={{ marginTop: 0, marginBottom: '1rem' }}>📝 Instructions:</h4>
                    <ol style={{ margin: 0, paddingLeft: '1.5rem', lineHeight: '1.8' }}>
                      <li>Copy the configuration below</li>
                      <li>Replace <code style={{ 
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                        padding: '2px 6px',
                        borderRadius: '4px'
                      }}>YOUR_ACCESS_TOKEN_HERE</code> with your actual access token</li>
                      <li>You can create an access token in the <strong>Account</strong> tab under "Access Tokens"</li>
                      <li>Paste the configuration into your Cursor IDE MCP settings</li>
                    </ol>
                  </div>

                  <div className="code-block-container">
                    <div className="code-block-header">
                      <span>AI-MCP-Bio Proxy Configuration</span>
                      <div className="code-block-actions">
                        <button className="copy-btn" onClick={() => copyToClipboard(proxyJsonConfig)}>
                          Copy
                        </button>
                      </div>
                    </div>
                    <pre className="code-block">
                      <code>{proxyJsonConfig}</code>
                    </pre>
                  </div>
                </div>

                {/* Available Tools Preview */}
                <div className="tools-preview">
                  <h4>Available Tools in Active Profile</h4>
                  {activeProfile ? (
                    <div>
                      <p>Your AI assistant will have access to these tools from your active profile:</p>
                      
                      {/* Resource Count Display */}
                      <div className="profile-resources">
                        <div className="resource-count">
                          <span className="resource-item">
                            🖥️ MCP Servers: {activeProfile.mcpServers?.filter(({ mcpServer }) => mcpServer?.status === 'AVAILABLE').length || 0}
                          </span>
                          <span className="resource-item">
                            ⚡ Workflows: {activeProfile.workflows?.filter(({ workflow }) => workflow?.status === 'AVAILABLE').length || 0}
                          </span>
                        </div>
                      </div>
                      
                      {loadingTools ? (
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                          <p>Loading tools...</p>
                        </div>
                      ) : profileTools ? (
                        <div className="resources-tools-container">
                          {/* MCP Servers with Tools */}
                          {profileTools.mcpServers && profileTools.mcpServers.length > 0 && (
                            <div className="resources-section">
                              <h5 style={{ marginBottom: '1rem', color: isDarkMode ? '#e0e0e0' : '#333' }}>
                                🖥️ MCP Servers
                              </h5>
                              {profileTools.mcpServers.map((resource) => {
                                const isExpanded = expandedResources.has(`mcp-${resource.id}`);
                                const toolCount = resource.tools?.length || 0;
                                return (
                                  <div key={resource.id} className="resource-card" style={{
                                    marginBottom: '1rem',
                                    border: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
                                    borderRadius: '8px',
                                    overflow: 'hidden'
                                  }}>
                                    <div 
                                      className="resource-header" 
                                      onClick={() => toggleResource(`mcp-${resource.id}`)}
                                      style={{
                                        padding: '1rem',
                                        cursor: 'pointer',
                                        backgroundColor: isDarkMode ? '#2d2d2d' : '#f8f9fa',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        borderBottom: isExpanded ? `1px solid ${isDarkMode ? '#555' : '#e9ecef'}` : 'none'
                                      }}
                                    >
                                      <div>
                                        <h6 style={{ margin: 0, color: isDarkMode ? '#e0e0e0' : '#333' }}>
                                          {resource.name}
                                        </h6>
                                        <small style={{ color: isDarkMode ? '#ccc' : '#666' }}>
                                          {resource.endpoint}
                                        </small>
                                        {toolCount > 0 && (
                                          <span style={{ 
                                            marginLeft: '0.5rem',
                                            color: isDarkMode ? '#4CAF50' : '#28a745',
                                            fontSize: '0.85rem'
                                          }}>
                                            ({toolCount} {toolCount === 1 ? 'tool' : 'tools'})
                                          </span>
                                        )}
                                      </div>
                                      <span style={{ fontSize: '1.2rem' }}>
                                        {isExpanded ? '▼' : '▶'}
                                      </span>
                                    </div>
                                    {isExpanded && (
                                      <div className="resource-tools" style={{
                                        padding: '1rem',
                                        backgroundColor: isDarkMode ? '#1e1e1e' : '#fff'
                                      }}>
                                        {resource.tools && resource.tools.length > 0 ? (
                                          <div className="tools-list">
                                            {resource.tools.map((tool, idx) => (
                                              <div key={idx} style={{
                                                padding: '0.75rem',
                                                marginBottom: '0.5rem',
                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
                                                borderRadius: '4px',
                                                border: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`
                                              }}>
                                                <strong style={{ color: isDarkMode ? '#e0e0e0' : '#333', display: 'block', marginBottom: '0.25rem' }}>
                                                  {tool.name}
                                                </strong>
                                                {tool.description && (
                                                  <p style={{ 
                                                    margin: '0.25rem 0',
                                                    color: isDarkMode ? '#ccc' : '#666',
                                                    fontSize: '0.9rem'
                                                  }}>
                                                    {tool.description}
                                                  </p>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <p style={{ color: isDarkMode ? '#ccc' : '#666', fontStyle: 'italic' }}>
                                            {resource.error ? `Error: ${resource.error}` : 'No tools available'}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Workflows with Tools */}
                          {profileTools.workflows && profileTools.workflows.length > 0 && (
                            <div className="resources-section" style={{ marginTop: '2rem' }}>
                              <h5 style={{ marginBottom: '1rem', color: isDarkMode ? '#e0e0e0' : '#333' }}>
                                ⚡ Workflows
                              </h5>
                              {profileTools.workflows.map((resource) => {
                                const isExpanded = expandedResources.has(`workflow-${resource.id}`);
                                const toolCount = resource.tools?.length || 0;
                                return (
                                  <div key={resource.id} className="resource-card" style={{
                                    marginBottom: '1rem',
                                    border: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`,
                                    borderRadius: '8px',
                                    overflow: 'hidden'
                                  }}>
                                    <div 
                                      className="resource-header" 
                                      onClick={() => toggleResource(`workflow-${resource.id}`)}
                                      style={{
                                        padding: '1rem',
                                        cursor: 'pointer',
                                        backgroundColor: isDarkMode ? '#2d2d2d' : '#f8f9fa',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        borderBottom: isExpanded ? `1px solid ${isDarkMode ? '#555' : '#e9ecef'}` : 'none'
                                      }}
                                    >
                                      <div>
                                        <h6 style={{ margin: 0, color: isDarkMode ? '#e0e0e0' : '#333' }}>
                                          {resource.name}
                                        </h6>
                                        <small style={{ color: isDarkMode ? '#ccc' : '#666' }}>
                                          {resource.endpoint}
                                        </small>
                                        {toolCount > 0 && (
                                          <span style={{ 
                                            marginLeft: '0.5rem',
                                            color: isDarkMode ? '#4CAF50' : '#28a745',
                                            fontSize: '0.85rem'
                                          }}>
                                            ({toolCount} {toolCount === 1 ? 'tool' : 'tools'})
                                          </span>
                                        )}
                                      </div>
                                      <span style={{ fontSize: '1.2rem' }}>
                                        {isExpanded ? '▼' : '▶'}
                                      </span>
                                    </div>
                                    {isExpanded && (
                                      <div className="resource-tools" style={{
                                        padding: '1rem',
                                        backgroundColor: isDarkMode ? '#1e1e1e' : '#fff'
                                      }}>
                                        {resource.tools && resource.tools.length > 0 ? (
                                          <div className="tools-list">
                                            {resource.tools.map((tool, idx) => (
                                              <div key={idx} style={{
                                                padding: '0.75rem',
                                                marginBottom: '0.5rem',
                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
                                                borderRadius: '4px',
                                                border: `1px solid ${isDarkMode ? '#555' : '#e9ecef'}`
                                              }}>
                                                <strong style={{ color: isDarkMode ? '#e0e0e0' : '#333', display: 'block', marginBottom: '0.25rem' }}>
                                                  {tool.name}
                                                </strong>
                                                {tool.description && (
                                                  <p style={{ 
                                                    margin: '0.25rem 0',
                                                    color: isDarkMode ? '#ccc' : '#666',
                                                    fontSize: '0.9rem'
                                                  }}>
                                                    {tool.description}
                                                  </p>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <p style={{ color: isDarkMode ? '#ccc' : '#666', fontStyle: 'italic' }}>
                                            No tools available
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Show message if no resources at all */}
                          {profileTools && 
                           (!profileTools.mcpServers || profileTools.mcpServers.length === 0) && 
                           (!profileTools.workflows || profileTools.workflows.length === 0) && (
                            <div className="no-tools-message">
                              <p>No tools available in this profile. Add MCP servers and workflows to your profile to see them here.</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="no-tools-message">
                          <p>Unable to load tools. Please try refreshing the page.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p>Please select an active profile to see available tools.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 'workspace':
        return (
          <div className="tab-content">
            <Workspace />
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="user-profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-page" style={{ paddingTop: '0' }}>
      <div className="container">
        {/* Header Section with Logo and Title */}
        <div style={{ marginBottom: '3rem' }}>
          {/* Centered Title */}
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <h1 style={{ 
              fontSize: '3rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              color: isDarkMode ? 'white' : '#333'
            }}>
              User Dashboard
            </h1>
          </div>
          
          {/* Subheader text - centered on the page */}
          <div style={{ 
            marginBottom: '1.5rem'
          }}>
            <p style={{ 
              fontSize: '1.2rem', 
              color: isDarkMode ? '#B0B0B0' : '#666',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6',
              textAlign: 'center'
            }}>
              Manage your account settings, create and configure profiles, and connect your bioinformatics tools to AI assistants for seamless research workflows.
            </p>
          </div>
          
          {/* User welcome badge */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              borderRadius: '20px',
              border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'}`
            }}>
              <span style={{ 
                color: isDarkMode ? 'white' : '#333',
                fontSize: '0.9rem',
                fontWeight: '500'
              }}>
                Welcome, {profileData?.username} ({profileData?.role})
              </span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => setActiveTab('account')}
          >
            Account
          </button>
          <button
            className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button
            className={`tab-button ${activeTab === 'connect-ai' ? 'active' : ''}`}
            onClick={() => setActiveTab('connect-ai')}
          >
            Connect to AI
          </button>
          <button
            className={`tab-button ${activeTab === 'workspace' ? 'active' : ''}`}
            onClick={() => setActiveTab('workspace')}
          >
            Workspace
          </button>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </div>
  );
};

export default UserProfile;
